import { FaRegEdit, FaSearch } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import { BsSortAlphaDown, BsSortAlphaDownAlt } from "react-icons/bs";
import "./style.css";

import {
  CBadge,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GET_API_CALL } from "../../utils/services";
import { ENDPOINTS } from "../../utils/endPoints";
import { EMAIL_STATUS_CONFIG } from "../../utils/contentTypes";
import { formatSlugToLabel } from "../../utils/commonFunctions";
import CustomPagination from "../../components/customPagination";

const DEFAULT_LIMIT = 10;

const SORT_ACCESSORS = {
  name: (template) => template.label,
  subject: (template) => template.subject || "",
  status: (template) => template.status || "",
};

const EmailTemplates = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState(1);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  function getEmailListing() {
    setLoading(true);
    GET_API_CALL(ENDPOINTS.EMAIL.LIST, "", (res) => {
      console.log("response in email listing :", res);
      if (res.isSuccess) {
        setEmails(
          (res.data.data || []).map((template) => ({
            ...template,
            label: formatSlugToLabel(template.type),
          }))
        );
      } else {
        console.log("Error in email listing :", res);
      }
      setLoading(false);
    });
  }

  useEffect(() => {
    getEmailListing();
  }, []);

  const filteredEmails = useMemo(() => {
    const query = searchKey.trim().toLowerCase();
    if (!query) return emails;
    return emails.filter(
      ({ label, subject }) =>
        label.toLowerCase().includes(query) ||
        (subject || "").toLowerCase().includes(query)
    );
  }, [emails, searchKey]);

  const sortedEmails = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortBy];
    if (!accessor) return filteredEmails;
    return [...filteredEmails].sort(
      (a, b) =>
        accessor(a).localeCompare(accessor(b), undefined, {
          sensitivity: "base",
        }) * sortOrder
    );
  }, [filteredEmails, sortBy, sortOrder]);

  const totalPage = Math.max(Math.ceil(sortedEmails.length / limit), 1);
  const currentPage = Math.min(pageNo, totalPage);
  const paginatedEmails = sortedEmails.slice(
    (currentPage - 1) * limit,
    currentPage * limit
  );

  const handleSearchChange = (e) => {
    setSearchKey(e.target.value.trimStart());
    setPageNo(1);
  };

  const handleClearSearch = () => {
    setSearchKey("");
    setPageNo(1);
  };

  const handleSort = (field) => {
    setSortOrder(sortBy === field ? -sortOrder : 1);
    setSortBy(field);
    setPageNo(1);
  };

  const pageLimitHandler = (e) => {
    setLimit(Number(e.target.value));
    setPageNo(1);
  };

  const renderSortIcon = (field) => (
    <span onClick={() => handleSort(field)}>
      {sortBy === field && sortOrder === -1 ? (
        <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
      ) : (
        <BsSortAlphaDown className="sort-icons cursor-pointer" />
      )}
    </span>
  );

  const renderTableBody = () => {
    if (loading) {
      return (
        <CTableBody>
          <CTableRow>
            <CTableDataCell colSpan={5} className="text-center py-5">
              <CSpinner color="primary" />
              <div className="mt-2">Loading email templates...</div>
            </CTableDataCell>
          </CTableRow>
        </CTableBody>
      );
    }

    if (paginatedEmails.length === 0) {
      return (
        <CTableBody>
          <CTableRow>
            <CTableDataCell colSpan={5} className="text-center py-5 text-muted">
              No email templates found.
            </CTableDataCell>
          </CTableRow>
        </CTableBody>
      );
    }

    return (
      <CTableBody>
        {paginatedEmails.map((template, index) => {
          const { _id, label, subject, status } = template;
          const statusInfo = EMAIL_STATUS_CONFIG[status] || {
            label: "Unknown",
            color: "secondary",
          };

          return (
            <CTableRow key={_id} className="email-table-data-text">
              <CTableDataCell className="email-table-serial-cell text-center">
                {(currentPage - 1) * limit + index + 1}
              </CTableDataCell>

              <CTableDataCell title={label}>{label || "N/A"}</CTableDataCell>

              <CTableDataCell title={subject}>
                {subject || "N/A"}
              </CTableDataCell>

              <CTableDataCell>
                <CBadge color={statusInfo.color} className="email-status-badge">
                  {statusInfo.label}
                </CBadge>
              </CTableDataCell>

              <CTableDataCell className="text-center">
                <FaRegEdit
                  className="email-action-icon cursor-pointer"
                  onClick={() =>
                    navigate(`/emails/${_id}`, {
                      state: template,
                    })
                  }
                />
              </CTableDataCell>
            </CTableRow>
          );
        })}
      </CTableBody>
    );
  };

  const renderView = () => {
    return (
      <div className="email-template-container">
        <div className="email-table-header-container">
          <h3 className="email-template-heading text-primary mb-0">
            Email Templates List
          </h3>

          <div className="d-flex justify-content-end align-items-center gap-1">
            <CInputGroup style={{ width: "300px" }} size="sm">
              <CInputGroupText>
                <FaSearch />
              </CInputGroupText>
              <CFormInput
                value={searchKey}
                name="search"
                onChange={handleSearchChange}
                placeholder="Search template name or subject"
              />
              {searchKey && (
                <CInputGroupText
                  className="cursor-pointer"
                  onClick={handleClearSearch}
                >
                  <RxCross2 />
                </CInputGroupText>
              )}
            </CInputGroup>

            <div
              className="btn btn-primary btn-sm email-add-btn-container"
              onClick={() => navigate("/email-templates/add")}
            >
              <span>Add</span>

              <span className="email-add-icon">
                <MdAdd />
              </span>
            </div>
          </div>
        </div>

        <CTable hover responsive>
          <CTableHead className="text-nowrap border">
            <CTableRow>
              <CTableHeaderCell className="bg-body-tertiary text-center email-table-serial-cell">
                S. No.
              </CTableHeaderCell>

              <CTableHeaderCell
                className={`bg-body-tertiary email-table-name-cell ${sortBy === "name" ? "sort-highlight" : ""}`}
              >
                Template Name
                {renderSortIcon("name")}
              </CTableHeaderCell>

              <CTableHeaderCell
                className={`bg-body-tertiary email-table-subject-cell ${sortBy === "subject" ? "sort-highlight" : ""}`}
              >
                Email Subject
                {renderSortIcon("subject")}
              </CTableHeaderCell>

              <CTableHeaderCell
                className={`bg-body-tertiary email-table-status-cell ${sortBy === "status" ? "sort-highlight" : ""}`}
              >
                Status
                {renderSortIcon("status")}
              </CTableHeaderCell>

              <CTableHeaderCell className="bg-body-tertiary text-center email-table-action-cell">
                Actions
              </CTableHeaderCell>
            </CTableRow>
          </CTableHead>

          {renderTableBody()}
        </CTable>

        {!loading && sortedEmails.length ? (
          <CustomPagination
            limit={limit}
            limitHanlder={pageLimitHandler}
            pageNo={currentPage}
            pageChangeHanlder={setPageNo}
            totalPage={totalPage}
          />
        ) : null}
      </div>
    );
  };

  return <div>{renderView()}</div>;
};

export default EmailTemplates;
